import React from "react";
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { tokens, withAlpha } from '../../../Common/designTokens'; // added
import { ROW_HEIGHT, CELL_PADDING, RFQ_TABLE_COLUMNS } from "./RfqConstants";
import RfqTableRow from "./RfqTableRow";
import { ORDER_STATUS } from "../../../Constants/AppConstants";

const RfqTable = ({ 
  venues, 
  animationState, 
  isLoading, 
  isButtonEnabled, 
  onAction,
  hasAcceptedQuote,
  allQuotesRejected,
  isTrumidRfq,
  rfqStatus,
  rfqInquiryType,
  protocolName,
  // NEW props
  entryBy,
  currentUserId,
}) => {
  // Legacy behavior used both counter_accepted and expired
  const hasCounterAcceptedOrExpired = venues.some(venue => 
    venue.status.toLowerCase() === ORDER_STATUS.COUNTER_ACCEPTED || 
    venue.status.toLowerCase() === ORDER_STATUS.EXPIRED
  );

  const norm = (s) => (s ? String(s).trim().toUpperCase() : "");
  const isManualTraded = norm(rfqStatus) === "MANUAL_TRADED";
  // Quote-level flags
  const hasCounterAcceptedOnly = venues.some(v => v.status && v.status.toLowerCase() === ORDER_STATUS.COUNTER_ACCEPTED);
  const hasAcceptedOrAcked = venues.some(v => {
    const st = norm(v.status);
    return st === "ACCEPTED" || st === "ACCEPTANCE_ACKED";
  });

  // Helpers for enhanced RFQ order-level rules

  // Enhanced protocol action-column visibility per spec table
  const computeEnhancedActionColumnVisible = () => {
    if (isManualTraded) {
      return false;
    }

    const s = norm(rfqStatus);
    const inquiry = norm(rfqInquiryType);

    // Default visible
    let visible = true;

    switch (s) {
      case "SENT":
      case "REJECTED":
        // RFQ cannot expand; if rendered, hide column.
        visible = false;
        break;
      case "OPEN":
        // Show only if inquiry type is NOT BIN
        visible = inquiry !== "BIN";
        break;
      case "TRADED":
      case "EXPIRED":
      case "PARTIALLY FILLED":
        visible = false;
        break;
      case "DUE IN TIME OVER":
        visible = true;
        break;
      case "MANUAL_TAKE_OVER":
        visible = false; 
        break;
      case "CANCELED":
      case "CANCELED_ACKED":
        visible = false;
        break;
      default:
        visible = true;
        break;
    }

    // A COUNTER_ACCEPTED quote no longer hides the column: it stays visible with every button
    // disabled (RfqUtils.isButtonEnabled), like ACCEPTED / ACCEPTANCE_ACKED. Once the trade books,
    // the rfq is TRADED and the switch above hides it.

    // Keep column visible when any quote is ACCEPTED / ACCEPTANCE_ACKED
    // except when order status is TRADED or EXPIRED (these must remain hidden).
    if (hasAcceptedOrAcked && s !== "TRADED" && s !== "EXPIRED") {
      visible = true;
    }

    return visible;
  };

  // Final hide/show decision
  const hideActionColumn = isTrumidRfq
    ? !computeEnhancedActionColumnVisible()
    : (hasAcceptedQuote || hasCounterAcceptedOnly || allQuotesRejected || venues.some(v => v.status.toLowerCase() === ORDER_STATUS.EXPIRED));

  let displayColumns = hideActionColumn 
    ? RFQ_TABLE_COLUMNS.filter(c => c.id !== "action")
    : RFQ_TABLE_COLUMNS;

  // Keep quoteExpiryDtTime column present in the header regardless of protocol.
  // Row-level cells will show '---' when the value is undefined.
  // Determine whether the current user is the RFQ creator
  const canAct = React.useMemo(() => {
    if (entryBy === undefined || entryBy === null) return true; // if missing, keep existing behavior (allow buttons logic)
    // Coerce to string comparison for robustness
    return String(entryBy) === String(currentUserId);
  }, [entryBy, currentUserId]);

  const actionColumnWidth = React.useMemo(
    () => RFQ_TABLE_COLUMNS.find((c) => c.id === "action")?.width || "210px",
    []
  );

  return (
    <Box sx={{ position: "relative", display: "inline-block" }}>
      <Table
        size="small"
        sx={{
          width: "auto",
          backgroundColor: tokens.colors.bodyBackgroundColor,
          tableLayout: "fixed",
          marginLeft: 0,
          "& .MuiTableCell-root": {
            height: ROW_HEIGHT,
            padding: CELL_PADDING,
            verticalAlign: "middle",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          },
          "& .MuiTableRow-root": {
            opacity: animationState === "entered" ? 1 : 0,
            transition: "opacity 0.3s ease",
          },
        }}
      >
        <TableHead>
          <TableRow>
            {displayColumns.map((column, index) => (
              <TableCell
                key={column.id}
                sx={{
                color: tokens.colors.fontColour,
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  textAlign: column.align,
                  width: column.width,
                  borderRight:
                    index < displayColumns.length - 1
                    ? `1px solid ${tokens.colors.bodyBorderColor50}`
                      : "none",
                  position: "relative",
                  backgroundColor: tokens.colors.defaultGridRowColor,
                  borderBottom: `2px solid ${tokens.colors.bodyBorderColor50}`,
                }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {venues.length > 0 ? (
            venues.map((venue, index) => (
              <RfqTableRow
                key={index}
                venue={venue}
                index={index}
                animationState={animationState}
                isLoading={isLoading}
                // Pass the raw function; row will wrap it with context for enhanced protocols
                isButtonEnabled={isButtonEnabled}
                onAction={onAction}
                hasAcceptedQuote={hasAcceptedQuote} // FIX: use real accepted flag for price styling
                allQuotesRejected={allQuotesRejected}
                // NEW props
                isTrumidRfq={isTrumidRfq}
                venues={venues}
                actionColumnHidden={hideActionColumn}
                rfqStatus={rfqStatus}
                rfqInquiryType={rfqInquiryType}
                protocolName={protocolName}
                // NEW: pass down permission flag
                canAct={canAct}
              />
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={displayColumns.length}
                sx={{ textAlign: "center", color: tokens.colors.fontColour }}
              >
                Awaiting dealer quotes...
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {!hideActionColumn && !canAct && venues.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            top: ROW_HEIGHT,
            bottom: 0,
            right: 0,
            width: actionColumnWidth,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            px: 1,
            pointerEvents: "none",
            color: tokens.colors.fontColour80 || tokens.colors.fontColour,
            fontStyle: "italic",
            background: tokens.colors.defaultGridRowColor,
          }}
        >
          Only the user who placed this RFQ can take action
        </Box>
      )}
    </Box>
  );
};

export default RfqTable;
